import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

const POLL_INTERVAL_MS = 2000;

/** Shared polling logic behind every bulk-job status hook that fetches its status through a
 *  hand-built endpoint (not a generated TanStack Query option) — currently
 *  useBulkAddCounterpartyStatus and useBulkAssignCounterpartyStatus. Polls every
 *  POLL_INTERVAL_MS until the status reaches one of `terminalStatuses`, then invalidates the
 *  document list query and calls `onTerminal` once. */
export function useBulkJobStatus<TState extends { status: string }>({
	dealId,
	programmeId,
	jobId,
	queryKeyPrefix,
	buildStatusEndpoint,
	terminalStatuses,
	onTerminal,
}: {
	dealId: string;
	programmeId: string;
	jobId: string | null;
	queryKeyPrefix: string;
	buildStatusEndpoint: (dealId: string, programmeId: string, jobId: string) => string;
	terminalStatuses: readonly string[];
	onTerminal?: (state: TState) => void;
}) {
	const queryClient = useQueryClient();
	const documentListKey = formatUnicorn(
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_LIST_GET,
		{ dealId, programmeId }
	);

	const q = useQuery({
		queryKey: [queryKeyPrefix, dealId, programmeId, jobId],
		queryFn: async () => {
			const endpoint = buildStatusEndpoint(dealId, programmeId, jobId as string);
			const response = await makeRequest<ApiV1.Response<TState>>({
				endpoint,
				method: HTTP_METHOD.GET,
			});
			return response.payload as TState;
		},
		enabled: !!jobId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if (!data) {
				return POLL_INTERVAL_MS;
			}
			if (terminalStatuses.includes(data.status)) {
				return false;
			}
			return POLL_INTERVAL_MS;
		},
	});

	useEffect(() => {
		if (!q.isSuccess) {
			return;
		}
		void (async () => {
			const data = q.data;
			if (terminalStatuses.includes(data.status)) {
				await queryClient.invalidateQueries({ queryKey: [documentListKey] });
				onTerminal?.(data);
			}
		})();
	}, [q.isSuccess, q.data]);

	return q;
}
